import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Link, Route, Switch, useLocation } from "wouter";
import { ClaimPage } from "./auth/ClaimPage";
import { Button } from "./components/Button";
import { LoginPage } from "./auth/LoginPage";
import {
  PasswordResetConfirmPage,
  PasswordResetRequestPage,
} from "./auth/PasswordResetPages";
import { SignupPage } from "./auth/SignupPage";
import { AppShell } from "./components/AppShell";
import { Card } from "./components/Card";
import { EmptyState } from "./components/EmptyState";
import { Spinner } from "./components/Spinner";
import { ToastProvider } from "./components/Toast";
import { ClockinAdminPage } from "./features/clockin/AdminPage";
import { StemplePage } from "./features/clockin/StemplePage";
import { KoerselPage } from "./features/koersel/KoerselPage";
import { KioskPage, KioskPairingPage } from "./features/kiosk/KioskPage";
import { PingPage } from "./features/ping/PingPage";
import { LegalPage } from "./legal/LegalPage";
import { LegalDocumentsProvider } from "./legal/useLegalDocuments";
import { EnhederPage, MedarbejderEnhederPage } from "./pages/EnhederPage";
import { HomePage } from "./pages/HomePage";
import { IndstillingerPage } from "./pages/IndstillingerPage";
import { MedarbejderePage } from "./pages/MedarbejderePage";
import { KontoPage } from "./pages/KontoPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { useMe, type UserRole } from "./lib/useMe";

// Kun i dev-build: showcase-ruten og dens chunk tree-shakes ud af
// produktionsbuildet, fordi import.meta.env.DEV er statisk false dér.
const ComponentShowcase = import.meta.env.DEV
  ? lazy(() => import("./dev/ComponentShowcase"))
  : null;

export function NotFound() {
  return (
    <Card>
      <EmptyState
        title="Siden findes ikke"
        description="Adressen matcher ikke en side i AppSmeden."
        cta={<Link href="/">Gå til Overblik</Link>}
      />
    </Card>
  );
}

export function App() {
  return (
    <LegalDocumentsProvider>
      <ToastProvider>
        <Switch>
        {/* Fritstående sider (uden AppShell): auth-flows og claim. */}
        <Route path="/signup">
          <SignupPage />
        </Route>
        <Route path="/login">
          <LoginPage />
        </Route>
        <Route path="/glemt-password">
          <PasswordResetRequestPage />
        </Route>
        <Route path="/nyt-password/:token">
          {(params) => <PasswordResetConfirmPage token={params.token} />}
        </Route>
        <Route path="/jura/privatlivspolitik">
          <LegalPage slug="privatlivspolitik" />
        </Route>
        <Route path="/jura/databehandleraftale">
          <LegalPage slug="databehandleraftale" />
        </Route>
        <Route path="/claim/:token">
          <ClaimPage />
        </Route>
        <Route path="/kiosk/pair/:token">
          {(params) => <KioskPairingPage token={params.token} />}
        </Route>
        <Route path="/kiosk">
          <KioskPage />
        </Route>
        {/* Medarbejderens stemple-flade: fuldskærm, ingen AppShell-sidebar. */}
        <Route path="/stemple/enheder">
          <RoleGate allowed={["ejer", "administrator", "medarbejder"]}>
            <MedarbejderEnhederPage />
          </RoleGate>
        </Route>
        <Route path="/stemple">
          <RoleGate allowed={["ejer", "administrator", "medarbejder"]}>
            <StemplePage />
          </RoleGate>
        </Route>
        <Route>
          <AppShell>
            <Switch>
              <Route path="/">
                <RoleGate allowed={["ejer", "administrator"]}>
                  <HomePage />
                </RoleGate>
              </Route>
              <Route path="/medarbejdere">
                <RoleGate allowed={["ejer", "administrator"]}>
                  <MedarbejderePage />
                </RoleGate>
              </Route>
              <Route path="/onboarding">
                <RoleGate allowed={["ejer", "administrator"]}>
                  <OnboardingPage />
                </RoleGate>
              </Route>
              <Route path="/indstillinger">
                <RoleGate allowed={["ejer", "administrator"]}>
                  <IndstillingerPage />
                </RoleGate>
              </Route>
              <Route path="/enheder">
                <RoleGate allowed={["ejer", "administrator", "bogholder"]}>
                  <EnhederPage />
                </RoleGate>
              </Route>
              <Route path="/konto">
                <RoleGate allowed={["ejer", "administrator", "bogholder"]}>
                  <KontoPage />
                </RoleGate>
              </Route>
              {/* Featurens shell-side — nav-ruten /clockin fra manifestet. */}
              <Route path="/clockin">
                <RoleGate allowed={["ejer", "administrator", "bogholder"]}>
                  <ClockinAdminPage />
                </RoleGate>
              </Route>
              <Route path="/koersel">
                <RoleGate allowed={["ejer", "administrator", "bogholder", "medarbejder"]}>
                  <KoerselPage />
                </RoleGate>
              </Route>
              <Route path="/ping">
                <PingPage />
              </Route>
              {ComponentShowcase !== null && (
                <Route path="/dev/components">
                  <Suspense fallback={<Spinner large />}>
                    <ComponentShowcase />
                  </Suspense>
                </Route>
              )}
              <Route>
                <NotFound />
              </Route>
            </Switch>
          </AppShell>
        </Route>
        </Switch>
      </ToastProvider>
    </LegalDocumentsProvider>
  );
}

function startsideFor(rolle: UserRole): string {
  if (rolle === "medarbejder") return "/stemple";
  if (rolle === "bogholder") return "/clockin?fane=rapport";
  return "/";
}

export function RoleGate({
  allowed,
  children,
}: {
  allowed: readonly UserRole[];
  children: ReactNode;
}) {
  const { me, loading, error, reload } = useMe();
  const [, navigate] = useLocation();
  const denied = me !== null && !allowed.includes(me.rolle);

  useEffect(() => {
    if (denied && me !== null) {
      navigate(startsideFor(me.rolle), { replace: true });
    }
  }, [denied, me, navigate]);

  useEffect(() => {
    if (!loading && error === "unauthorized") {
      navigate("/login", { replace: true });
    }
  }, [error, loading, navigate]);

  if (loading || denied || error === "unauthorized") {
    return <Spinner large />;
  }
  if (error === "failed" || me === null) {
    return (
      <Card>
        <EmptyState
          title="Kunne ikke hente din adgang"
          description="Kontrollér forbindelsen, og prøv igen."
          cta={<Button onClick={reload}>Prøv igen</Button>}
        />
      </Card>
    );
  }
  return children;
}
